// TMOS Public Website Login Form Component
// Date: January 27, 2025
// Purpose: Minimalistic login form for public website homepage

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { authService } from '@/services/auth';
import { PublicLegalLinks } from '@/components/public/PublicLegalLinks';

interface LoginFormProps {
    onSuccess?: () => void;
    /** When true, do not navigate after login; caller controls post-login flow (e.g. close modal, retry action). */
    disableRedirect?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, disableRedirect }) => {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, setUser, setTokenState } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Check for token in URL (from admin password reset email)
    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            handleTokenLogin(token);
        }
    }, [searchParams]);
    
    const handleTokenLogin = async (token: string) => {
        setLoading(true);
        setError('');
        
        try {
            const authResponse = await authService.loginWithToken(token);
            
            // Update auth state - manually set user and token since we bypassed normal login
            setUser(authResponse.user);
            setTokenState(authResponse.token);
            
            // If password change is required, redirect to password change page
            if (authResponse.requirePasswordChange && !disableRedirect) {
                navigate('/change-password?required=true');
            } else if (!disableRedirect) {
                // Remove token from URL
                navigate('/dashboard', { replace: true });
            }
            
            if (onSuccess) {
                onSuccess();
            }
        } catch (err: any) {
            console.error('Token login error:', err);
            
            // Extract error message properly
            let errorMessage = 'Invalid or expired login link. Please request a new one.';
            
            if (err?.response?.data) {
                const errorData = err.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData?.error) {
                    errorMessage = typeof errorData.error === 'string' 
                        ? errorData.error 
                        : (errorData.error?.message || errorMessage);
                } else if (errorData?.message) {
                    errorMessage = typeof errorData.message === 'string' ? errorData.message : errorMessage;
                }
            } else if (err?.message) {
                errorMessage = typeof err.message === 'string' ? err.message : errorMessage;
            }
            
            setError(errorMessage);
            // Remove token from URL on error
            navigate('/', { replace: true });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Use the auth provider's login method which expects credentials
            const authResponse = await login({ email, password });

            // Pending actions first: password change, then complete purchase, else dashboard (unless redirects are disabled)
            if (authResponse.requirePasswordChange && !disableRedirect) {
                navigate('/change-password?required=true');
                onSuccess?.();
                return;
            }
            if (authResponse?.planAttempt && !disableRedirect) {
                const params = new URLSearchParams({ section: 'subscription' });
                if (authResponse.planAttempt.billingPlanId) params.set('planId', authResponse.planAttempt.billingPlanId);
                params.set('attemptId', authResponse.planAttempt.id);
                navigate(`/settings/profile?${params.toString()}`);
                onSuccess?.();
                return;
            }

            if (!disableRedirect) {
                // Always redirect to dashboard (or returnTo) after login; onSuccess e.g. closes modal
                const returnTo = new URLSearchParams(window.location.search).get('returnTo');
                navigate(returnTo || '/dashboard');
            }
            onSuccess?.();
        } catch (err: any) {
            console.error('Login error:', err);
            
            // Extract error message properly - handle different error response formats
            let errorMessage = 'Login failed. Please try again.';
            
            if (err?.response?.data) {
                const errorData = err.response.data;
                // Handle different error response formats
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData?.error) {
                    // Backend returns { error: { message: ..., code: ..., ... } }
                    errorMessage = typeof errorData.error === 'string' 
                        ? errorData.error 
                        : (errorData.error?.message || errorMessage);
                } else if (errorData?.message) {
                    errorMessage = typeof errorData.message === 'string' ? errorData.message : errorMessage;
                }
            } else if (err?.message) {
                errorMessage = typeof err.message === 'string' ? err.message : errorMessage;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto">
            <div className="space-y-4">
                {/* Email Input */}
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                        {t('website.home.sign_in.email')}
                    </label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border-2 border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-tab-inactive-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-text)] focus:border-[var(--color-primary-text)] transition-colors"
                        placeholder={t('website.home.sign_in.email_placeholder')}
                    />
                </div>

                {/* Password Input */}
                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                        {t('website.home.sign_in.password')}
                    </label>
                    <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border-2 border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-tab-inactive-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-text)] focus:border-[var(--color-primary-text)] transition-colors"
                        placeholder={t('website.home.sign_in.password_placeholder')}
                    />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="rounded-lg bg-[var(--color-error)]/10 border-2 border-[var(--color-error)] px-4 py-2 text-sm text-[var(--color-error)]">
                        {error}
                    </div>
                )}

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-[var(--color-primary-text)] px-6 py-3 text-base font-semibold text-[var(--color-primary-bg)] shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? t('website.home.sign_in.logging_in') : t('website.home.sign_in.login')}
                </button>

                {/* Forgot Password Link */}
                <div className="text-center">
                    <a
                        href="/forgot-password"
                        className="text-sm text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)] transition-colors underline"
                    >
                        {t('website.home.sign_in.forgot_password')}
                    </a>
                </div>

                {/* Legal: bottom of login surface (not header) — standard for auth UIs */}
                <PublicLegalLinks className="pt-3 border-t border-[var(--color-border)]" />
            </div>
        </form>
    );
};

